/**
 * LocaleTabs — 다국어 입력 탭 전환 핸들러.
 *
 * 마크업 컨벤션 (resources/views/admin/components/locale-tabs.php):
 *   <div data-locale-tabs>
 *     <button data-locale-target="ko" aria-selected="true">한국어</button>
 *     <button data-locale-target="en" aria-selected="false">English</button>
 *     <div data-locale-panel="ko">...</div>
 *     <div data-locale-panel="en" class="...--hidden">...</div>
 *   </div>
 *
 * 마지막 선택 locale 은 sessionStorage 에 저장하여 페이지 전환 후에도 유지.
 */
const STORAGE_KEY = "admin.locale-tabs.active";

export class LocaleTabs {
    private readonly root: HTMLElement;
    private readonly tabs: HTMLButtonElement[];
    private readonly panels: HTMLElement[];
    private readonly copyButton: HTMLButtonElement | null;
    private readonly copySource: HTMLSelectElement | null;

    constructor(root: HTMLElement) {
        this.root = root;
        this.tabs = Array.from(root.querySelectorAll<HTMLButtonElement>("[data-locale-target]"));
        this.panels = Array.from(root.querySelectorAll<HTMLElement>("[data-locale-panel]"));
        this.copyButton = root.querySelector<HTMLButtonElement>("[data-locale-copy-button]");
        this.copySource = root.querySelector<HTMLSelectElement>("[data-locale-copy-source]");
    }

    init(): void {
        if (this.root.dataset.localeTabsInited === "1") return;
        this.root.dataset.localeTabsInited = "1";
        if (this.tabs.length === 0) return;

        // 숨긴 패널의 required 필드는 display:none 이라 HTML5 검증 시 "not focusable" 로
        // submit 자체가 조용히 차단된다(예: en 탭 활성 상태에서 등록 → ko 의 required 가 막음).
        // → 보이는 패널만 required 유지, 숨긴 패널은 required 를 잠시 해제(값은 유지, 서버 검증은 그대로).
        // 활성 locale 을 먼저 결정한 뒤 1회만 동기화한다.
        const preferred = this.readPreferredLocale();
        const initialLocale =
            preferred !== null && this.tabs.some((t) => t.dataset.localeTarget === preferred)
                ? preferred
                : this.currentLocale();
        this.activate(initialLocale, false);

        for (const tab of this.tabs) {
            tab.addEventListener("click", (ev) => {
                ev.preventDefault();
                const target = tab.dataset.localeTarget;
                if (target) this.activate(target, true);
            });
        }
        this.copyButton?.addEventListener("click", () => this.copyFromSource());
    }

    /** 마크업상 현재 활성(--active) 탭의 locale. 없으면 첫 탭. */
    private currentLocale(): string {
        const active = this.tabs.find((t) =>
            t.classList.contains("admin-locale-tabs__tab--active"),
        );
        return (active ?? this.tabs[0])?.dataset.localeTarget ?? "";
    }

    private activate(locale: string, persist: boolean): void {
        for (const tab of this.tabs) {
            const isActive = tab.dataset.localeTarget === locale;
            tab.classList.toggle("admin-locale-tabs__tab--active", isActive);
            tab.setAttribute("aria-selected", isActive ? "true" : "false");
        }
        for (const panel of this.panels) {
            const isActive = panel.dataset.localePanel === locale;
            panel.classList.toggle("admin-locale-tabs__panel--hidden", !isActive);
            this.syncRequired(panel, isActive);
        }
        if (persist) {
            try {
                sessionStorage.setItem(STORAGE_KEY, locale);
            } catch {
                /* ignore storage errors */
            }
        }
    }

    /**
     * 숨긴 패널의 required 를 해제(data-was-required 로 보관), 보이는 패널은 복원.
     * disabled 가 아니라 required 만 토글하므로 값은 항상 제출된다(서버 검증이 진실의 근거).
     */
    private syncRequired(panel: HTMLElement, isActive: boolean): void {
        const fields = panel.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
            "input, textarea, select",
        );
        for (const field of fields) {
            if (isActive) {
                if (field.dataset.wasRequired === "1") {
                    field.required = true;
                    delete field.dataset.wasRequired;
                }
            } else if (field.required) {
                field.dataset.wasRequired = "1";
                field.required = false;
            }
        }
    }

    private readPreferredLocale(): string | null {
        try {
            return sessionStorage.getItem(STORAGE_KEY);
        } catch {
            return null;
        }
    }

    private copyFromSource(): void {
        const targetLocale = this.currentLocale();
        const sourceLocale = this.copySource?.value ?? "";
        if (!targetLocale || !sourceLocale || targetLocale === sourceLocale) return;

        const sourcePanel = this.panels.find((panel) => panel.dataset.localePanel === sourceLocale);
        const targetPanel = this.panels.find((panel) => panel.dataset.localePanel === targetLocale);
        if (!sourcePanel || !targetPanel) return;

        const sourceFields = sourcePanel.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
            "input[name], textarea[name], select[name]",
        );
        sourceFields.forEach((field) => {
            const sourceName = field.getAttribute("name") ?? "";
            const targetName = this.targetName(sourceName, sourceLocale, targetLocale);
            if (targetName === "") return;
            const target = targetPanel.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
                `[name="${CSS.escape(targetName)}"]`,
            );
            if (!target) return;
            this.copyField(field, target);
        });
        targetPanel.dispatchEvent(new CustomEvent("locale:copied", { bubbles: true, detail: { sourceLocale, targetLocale } }));
    }

    private targetName(sourceName: string, sourceLocale: string, targetLocale: string): string {
        const exact = `translations[${sourceLocale}]`;
        if (sourceName.includes(exact)) {
            return sourceName.replace(exact, `translations[${targetLocale}]`);
        }
        return "";
    }

    private copyField(
        source: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
        target: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
    ): void {
        if (source instanceof HTMLInputElement && target instanceof HTMLInputElement) {
            if (source.type === "checkbox" || source.type === "radio") {
                target.checked = source.checked;
            } else {
                target.value = source.value;
            }
        } else {
            target.value = source.value;
        }
        target.dispatchEvent(new Event("input", { bubbles: true }));
        target.dispatchEvent(new Event("change", { bubbles: true }));
        target.dispatchEvent(new CustomEvent("cms:set-value", { bubbles: true }));
    }

    static initAll(root: ParentNode = document): void {
        const roots = Array.from(root.querySelectorAll<HTMLElement>("[data-locale-tabs]"));
        for (const el of roots) {
            new LocaleTabs(el).init();
        }
    }
}
